import React from 'react';
import {
  Menu,
  MenuItem,
  Divider,
  Box,
  Typography,
  Avatar,
  ListItemIcon,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { colors, fonts, spacing, borderRadius, shadows } from '../../constants';
import { getRoleDisplayName } from '../../utils/roleUtils';
import { Navigate } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';

const UserMenuPopup = ({
  anchorEl,
  open,
  onClose,
  user,
  onSettingsClick,
  onLogoutClick,
  menuId,
  menuButtonId,
}) => {
  const displayName = user?.full_name || user?.name;
  const roleDisplayName = getRoleDisplayName(user?.role);
  const fallbackEmailLocalPart = roleDisplayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
  const fallbackEmail = `${fallbackEmailLocalPart || 'user'}@querymanagement...`;
  const navigate = useNavigate();

  const handleSettingsClick = () => {
   navigate('/dashboard/settings');
    onClose();
  };

  const handleLogoutClick = () => {
    onLogoutClick?.();
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      id={menuId}
      MenuListProps={{
        'aria-labelledby': menuButtonId,
        role: 'menu',
      }}
      disablePortal={false}
      container={() => document.body}
      PaperProps={{
        sx: {
          borderRadius: borderRadius.md,
          minWidth: '200px',
          maxWidth: '240px',
          marginTop: spacing.xs,
          boxShadow: shadows.lg,
          border: `1px solid ${colors.borderDefault}`,
          overflow: 'hidden',
          backgroundColor: colors.backgroundWhite,
        },
      }}
      transformOrigin={{ horizontal: 'left', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'transparent',
        },
      }}
      slotProps={{
        paper: {
          sx: {
            marginLeft: '8px',
          },
        },
      }}
    >
      {/* User Info Section */}
      <Box
        sx={{
          padding: `${spacing.xs} ${spacing.sm}`,
          backgroundColor: colors.backgroundWhite,
          borderBottom: `1px solid ${colors.borderLight}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              background: colors.primaryGradient,
              fontSize: fonts.fontSizeSm,
              fontWeight: fonts.weightBold,
              color: colors.textWhite,
              border: 'none',
              boxShadow: `0 2px 8px ${colors.primaryShadow}`,
            }}
          >
            {(displayName?.split(' ').map(n => n.charAt(0)).join('').substring(0, 2) || 'U').toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: fonts.fontSizeSm,
                fontWeight: fonts.weightSemiBold,
                color: colors.textPrimaryDark,
                lineHeight: 1.2,
                mb: '1px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayName || 'User'}
            </Typography>
            <Typography
              sx={{
                fontSize: fonts.fontSizeXs,
                color: colors.textSecondary,
                lineHeight: 1.1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {user?.email || fallbackEmail}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu Items */}
      <Box sx={{ padding: '2px 0' }}>
        <MenuItem
          onClick={handleSettingsClick}
          sx={{
            padding: `${spacing.xs} ${spacing.sm}`,
            minHeight: '36px',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: colors.backgroundSoft,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: colors.textSecondary,
            }}
          >
            <SettingsIcon sx={{ fontSize: '16px' }} />
          </ListItemIcon>
          <Typography
            sx={{
              fontSize: fonts.fontSizeSm,
              color: colors.textPrimaryDark,
              fontWeight: fonts.weightMedium,
            }}
          >
            Settings
          </Typography>
        </MenuItem>
      </Box>

      <Divider sx={{ margin: '2px 0', borderColor: colors.borderLight }} />

      {/* Logout Item */}
      <Box sx={{ padding: '2px 0', pb: spacing.xs }}>
        <MenuItem
          onClick={handleLogoutClick}
          sx={{
            padding: `${spacing.xs} ${spacing.sm}`,
            minHeight: '36px',
            borderRadius: 0,
            '&:hover': {
              backgroundColor: colors.backgroundSoft,
            },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 28,
              color: colors.textSecondary,
            }}
          >
            <LogoutIcon sx={{ fontSize: '16px' }} />
          </ListItemIcon>
          <Typography
            sx={{
              fontSize: fonts.fontSizeSm,
              color: colors.textPrimaryDark,
              fontWeight: fonts.weightMedium,
            }}
          >
            Logout
          </Typography>
        </MenuItem>
      </Box>
    </Menu>
  );
};

export default UserMenuPopup;


